import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { Clock, FileSignature } from 'lucide-react';
import type { RegionalLicense } from '../../types/cultivar';

interface PendingLicenseBannerProps {
  pendingLicenses: RegionalLicense[];
}

/**
 * Shown to a licensee when they have one or more issued licences awaiting
 * their signature. Clicking through opens the agreement, which is the only
 * surface where a licence can actually be signed (electronically) or
 * declined. There is no inline "confirm" — every active licence requires a
 * captured signature.
 */
function PendingLicenseBanner({ pendingLicenses }: PendingLicenseBannerProps) {
  const navigate = useNavigate();

  if (pendingLicenses.length === 0) return null;

  const first = pendingLicenses[0];
  const more = pendingLicenses.length - 1;

  return (
    <div className="mb-6 bg-warning-50 border border-warning-200 rounded-xl overflow-hidden">
      <div className="px-6 py-4 flex items-center justify-between bg-warning-100">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-warning-200 flex items-center justify-center">
            <FileSignature className="h-5 w-5 text-warning-700" />
          </div>
          <div>
            <p className="font-semibold text-warning-900">
              {pendingLicenses.length} licence{pendingLicenses.length > 1 ? 's' : ''} awaiting your signature
            </p>
            <p className="text-sm text-warning-700">
              Open the agreement to sign electronically or upload a countersigned PDF.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-warning-200">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-neutral-900 truncate">
                {first.cultivar_trade_name || first.cultivar_name}
              </p>
            </div>
            <p className="text-sm text-neutral-600 truncate">
              From <span className="font-medium">{first.licensor_org_name}</span>
              {first.region && ` • ${first.region}`}
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/cultivar/licences/${first.id}/agreement`)}
          >
            Open agreement
          </Button>
        </div>
      </div>

      {more > 0 && (
        <div className="px-6 py-2 bg-warning-100 text-sm text-warning-700 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>And {more} more awaiting signature…</span>
        </div>
      )}
    </div>
  );
}

export default PendingLicenseBanner;
